import type { ReactNode } from 'react';
import { ThemeToggle } from './ThemeToggle.js';
import { useTheme } from './useTheme.js';
import type { AuthState } from './GlobalShell.js';
import { SIGN_IN_URL } from './App.js';

interface HeaderProps {
  authState: AuthState;
  userEmail?: string;
  currentPage: 'home' | 'search' | 'admin';
}

export function Header({
  authState,
  userEmail,
  currentPage,
}: HeaderProps): ReactNode {
  const { theme, toggleTheme } = useTheme();

  // DEF-LIVE-1: truncate the visible label only; the full value stays in `title`.
  const MAX_LABEL_LEN = 30;
  const displayLabel =
    userEmail && userEmail.length > MAX_LABEL_LEN
      ? `${userEmail.slice(0, MAX_LABEL_LEN - 1)}…`
      : userEmail;

  return (
    <header
      className="global-header"
      role="banner"
      data-testid="global-shell-header"
    >
      {/* Brand link — navigates to home */}
      <a
        href="/"
        className="global-header__brand"
        aria-label="RepoLens home"
        data-testid="brand-link"
      >
        RepoLens
      </a>

      {/* Nav search link */}
      <a
        href="/search"
        className="global-header__nav-search"
        aria-label="Search"
        aria-current={currentPage === 'search' ? 'page' : undefined}
        data-testid="nav-search-link"
      >
        Search
      </a>

      {/* Nav admin link */}
      <a
        href="/admin"
        className="global-header__nav-admin"
        aria-label="Admin"
        aria-current={currentPage === 'admin' ? 'page' : undefined}
        data-testid="nav-admin-tab"
      >
        Admin
      </a>

      {/* Spacer */}
      <span className="global-header__spacer" aria-hidden="true" />

      {/* Auth strip — renders nothing during loading to avoid layout shift */}
      {authState !== 'loading' && (
        <div className="global-header__auth">
          {authState === 'signed-out' && (
            <a
              href={SIGN_IN_URL}
              className="global-header__signin"
              aria-label="Sign in"
              data-testid="signin-btn"
            >
              Sign in
            </a>
          )}
          {authState === 'signed-in' && (
            <>
              {userEmail && (
                <span
                  className="global-header__user-label"
                  title={userEmail}
                  data-testid="header-user-label"
                >
                  {displayLabel}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Theme toggle — far right */}
      <ThemeToggle theme={theme} onToggle={toggleTheme} />
    </header>
  );
}
